import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Merge, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import type { Tag, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { describeRejection } from "@/modules/settings/errors";

const TAGS_QUERY_KEY = ["settings", "tags"];

/**
 * A `blocked` rename rejection that names another tag to merge into instead
 * — `renameTag`'s own doc comment in `@estelle/domain`. `details` is
 * `unknown` on the shared `WriteRejection` type, so this is the one place
 * that narrows it back to the shape the API and fixture adapters both send.
 */
function mergeCandidate(
  rejection: WriteRejection,
): { id: string; name: string } | null {
  if (rejection.kind !== "blocked") return null;
  const details = rejection.details;
  if (typeof details !== "object" || details === null) return null;
  const mergeInto = (details as { mergeInto?: unknown }).mergeInto;
  if (typeof mergeInto !== "object" || mergeInto === null) return null;
  const { id, name } = mergeInto as { id?: unknown; name?: unknown };
  return typeof id === "string" && typeof name === "string"
    ? { id, name }
    : null;
}

function usageSentence(usage: Tag["usage"]): string {
  const parts: string[] = [];
  if (usage.contact > 0) {
    parts.push(
      `${String(usage.contact)} contact${usage.contact === 1 ? "" : "s"}`,
    );
  }
  if (usage.task > 0) {
    parts.push(`${String(usage.task)} task${usage.task === 1 ? "" : "s"}`);
  }
  if (usage.customer > 0) {
    parts.push(
      `${String(usage.customer)} customer${usage.customer === 1 ? "" : "s"}`,
    );
  }
  const list =
    parts.length > 2
      ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1] ?? ""}`
      : parts.join(" and ");
  return parts.length > 0
    ? `Used by ${list}.`
    : "Not currently used by any contact, task or customer.";
}

/**
 * Settings → Tags (spec §8, `tags`/`taggables`, #405) — list, rename, merge
 * and delete the ERP-owned tag vocabulary.
 *
 * **ERP-owned only.** Contacts, tasks and customers (#447) write through
 * `taggables`; orders and products mirror their own tags from Shopify onto a plain column and
 * never touch it (CLAUDE.md: no ERP surface changes a mirrored value without
 * Shopify's say-so first) — the note below says so on the screen itself
 * rather than leaving someone to discover it by renaming a tag here and
 * finding no order changed.
 */
export function Tags() {
  const data = useDataSource();
  const queryClient = useQueryClient();

  /*
    The whole vocabulary, not the first page of it (#473). `listTags` answers a
    `Page`, and a page is not the vocabulary: the fixture adapter hands back 25
    and a `nextCursor`, which left every tag after the 25th by name out of the
    list — and out of the merge dropdown, which needs all of them. The API
    answers every tag in one response, so there this is one request.
  */
  const tagsQuery = useQuery({
    queryKey: TAGS_QUERY_KEY,
    queryFn: () => fetchAllPages((query) => data.settings.listTags(query)),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: TAGS_QUERY_KEY });
  }

  const [renaming, setRenaming] = useState<{
    id: string;
    draft: string;
  } | null>(null);
  const [renameRejection, setRenameRejection] = useState<WriteRejection | null>(
    null,
  );

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      data.settings.renameTag(id, name),
    onSuccess: (result) => {
      if (!result.ok) {
        setRenameRejection(result.rejection);
        return;
      }
      setRenaming(null);
      setRenameRejection(null);
      invalidate();
    },
  });

  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeRejection, setMergeRejection] = useState<WriteRejection | null>(
    null,
  );

  const mergeMutation = useMutation({
    mutationFn: ({
      sourceId,
      targetId,
    }: {
      sourceId: string;
      targetId: string;
    }) => data.settings.mergeTags(sourceId, targetId),
    onSuccess: (result) => {
      if (!result.ok) {
        setMergeRejection(result.rejection);
        return;
      }
      setMergeSourceId(null);
      setMergeTargetId("");
      setMergeRejection(null);
      invalidate();
    },
  });

  function openMerge(sourceId: string, targetId = "") {
    setRenaming(null);
    setRenameRejection(null);
    setMergeRejection(null);
    setMergeTargetId(targetId);
    setMergeSourceId(sourceId);
  }

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteRejection, setDeleteRejection] = useState<WriteRejection | null>(
    null,
  );

  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.settings.deleteTag(id),
    onSuccess: (result) => {
      if (!result.ok) {
        setDeleteRejection(result.rejection);
        return;
      }
      setDeletingId(null);
      setDeleteRejection(null);
      invalidate();
    },
  });

  if (tagsQuery.isPending) return <TableSkeleton rows={8} />;

  if (tagsQuery.isError) {
    return (
      <LoadFailed
        message={tagsQuery.error.message}
        onRetry={() => {
          void tagsQuery.refetch();
        }}
      />
    );
  }

  const tagList = tagsQuery.data;
  const mergeSource = tagList.find((tag) => tag.id === mergeSourceId) ?? null;
  const mergeTargets = tagList.filter((tag) => tag.id !== mergeSourceId);
  const deletingTag = tagList.find((tag) => tag.id === deletingId) ?? null;

  return (
    <div className="max-w-2xl">
      <div className="mb-3 rounded-md border border-dashed p-3 text-sm">
        <p className="font-medium">Contact, task and customer tags only.</p>
        <p className="text-muted-foreground">
          Order and product tags mirror from Shopify — edit those from the order
          or product itself. Renaming, merging or deleting a tag here never
          touches Shopify. It does change the tag on every contact, task and
          customer that carries it.
        </p>
      </div>

      {tagList.length === 0 ? (
        <p className="text-muted-foreground p-4 text-sm">
          No contact, task or customer carries a tag yet.
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Tag
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Contacts
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Tasks
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Customers
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tagList.map((tag) => {
              const isRenaming = renaming?.id === tag.id;
              const isDeleting =
                deleteMutation.isPending && deleteMutation.variables === tag.id;
              const candidate =
                isRenaming && renameRejection
                  ? mergeCandidate(renameRejection)
                  : null;

              return (
                <tr key={tag.id} className="border-b last:border-b-0">
                  <td className="px-3 py-1.5">
                    {isRenaming ? (
                      <form
                        className="flex items-center gap-1"
                        onSubmit={(event) => {
                          event.preventDefault();
                          const trimmed = renaming.draft.trim();
                          if (trimmed === "") return;
                          renameMutation.mutate({ id: tag.id, name: trimmed });
                        }}
                      >
                        <Input
                          autoFocus
                          className="h-8"
                          value={renaming.draft}
                          onChange={(event) => {
                            setRenaming({
                              id: tag.id,
                              draft: event.target.value,
                            });
                          }}
                        />
                        <Button
                          type="submit"
                          size="icon-sm"
                          variant="ghost"
                          disabled={
                            renaming.draft.trim() === "" ||
                            renameMutation.isPending
                          }
                        >
                          <Check />
                        </Button>
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => {
                            setRenaming(null);
                            setRenameRejection(null);
                          }}
                        >
                          <X />
                        </Button>
                      </form>
                    ) : (
                      <Badge variant="outline" className="font-normal">
                        {tag.name}
                      </Badge>
                    )}
                    {isRenaming && renameRejection ? (
                      <div className="mt-1 space-y-1">
                        <p className="text-destructive text-xs">
                          {describeRejection(renameRejection)}
                        </p>
                        {candidate ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              openMerge(tag.id, candidate.id);
                            }}
                          >
                            <Merge />
                            Merge into &ldquo;{candidate.name}&rdquo; instead
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {tag.usage.contact}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {tag.usage.task}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {tag.usage.customer}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Rename ${tag.name}`}
                        onClick={() => {
                          setRenaming({ id: tag.id, draft: tag.name });
                          setRenameRejection(null);
                        }}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Merge ${tag.name} into another tag`}
                        disabled={tagList.length < 2}
                        onClick={() => {
                          openMerge(tag.id);
                        }}
                      >
                        <Merge />
                      </Button>
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`Delete ${tag.name}`}
                        disabled={isDeleting}
                        onClick={() => {
                          setDeleteRejection(null);
                          setDeletingId(tag.id);
                        }}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Dialog
        open={mergeSource !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMergeSourceId(null);
            setMergeRejection(null);
          }
        }}
      >
        <DialogContent
          title={mergeSource ? `Merge "${mergeSource.name}"` : "Merge tag"}
          description="Every contact and task carrying this tag carries the target tag instead. This cannot be undone."
        >
          <div className="space-y-4">
            <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Merge into…" />
              </SelectTrigger>
              <SelectContent>
                {mergeTargets.map((target) => (
                  <SelectItem key={target.id} value={target.id}>
                    {target.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {mergeRejection ? (
              <p className="text-destructive text-sm">
                {describeRejection(mergeRejection)}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                disabled={mergeTargetId === "" || mergeMutation.isPending}
                onClick={() => {
                  if (!mergeSourceId) return;
                  mergeMutation.mutate({
                    sourceId: mergeSourceId,
                    targetId: mergeTargetId,
                  });
                }}
              >
                {mergeMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                Merge
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deletingTag !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingId(null);
            setDeleteRejection(null);
          }
        }}
      >
        <DialogContent
          title={deletingTag ? `Delete "${deletingTag.name}"?` : "Delete tag"}
          description="This cannot be undone."
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              {deletingTag ? usageSentence(deletingTag.usage) : null}
            </p>
            {deleteRejection ? (
              <p className="text-destructive text-sm">
                {describeRejection(deleteRejection)}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (!deletingId) return;
                  deleteMutation.mutate(deletingId);
                }}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
